import PropTypes from 'prop-types';
import Link from 'next/link';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { getContent } from '@/utils/helper';
import {
  analytics,
  getClickFooterNavigation,
  getClickSocialDetail,
} from '@/utils/analytics';

import Icon from '@/atoms/Icon/Icon';
import WestholmeLogo from '@/atoms/Logo/WestholmeLogo';
import DDLogo from '@/atoms/Logo/DDLogo';
import Text from '@/atoms/Text/Text';
import { CtaWrapper } from '@/molecules/CTA';
import { normalizedUrl } from '@/molecules/CTA/CTA.constants';
import LangSwitcher from '@/molecules/LangSwitcher/LangSwitcher';
import ButtonDropdown from '@/molecules/ButtonDropdown/ButtonDropdown';

const { publicRuntimeConfig = {} } = getConfig() || {};

function Footer({ entries = [], fields = null, showVersion }) {
  const {
    name,
    navigation,
    showLogo,
    copyrightText,
    socialLinks: socials,
    footerLinks: footerUrls,
  } = fields;

  const { domainLocales, locales, asPath, locale, defaultLocale } = useRouter()

  const navLinks = navigation
    ? getContent(navigation.sys.id, entries)?.fields.links.map((link) =>
        getContent(link.sys.id, entries)
      )
    : [];

  const navigationMenu =
    navLinks.length > 0
      ? navLinks.map((link) => {
          const menuContent = getContent(link.sys.id, entries)?.fields;

          // get page preview & url per link
          const page =
            menuContent?.pages && menuContent?.pages.length
              ? getContent(menuContent?.pages[0].sys.id, entries)
              : menuContent?.page
              ? getContent(menuContent?.page?.sys?.id, entries)
              : null;

          return {
            ...menuContent,
            pageUrl: page?.fields?.slug,
            pageContent: {
              cloudinaryPreviewAssets: page?.fields?.cloudinaryPreviewAssets,
              previewAsset: page?.fields?.previewAsset,
              previewText: getContent(
                page?.fields?.previewText?.sys?.id,
                entries
              ),
            },
          };
        })
      : [];

  const socialLinks = socials?.length
    ? socials.map((social) => getContent(social.sys.id, entries))
    : [];

  const footerLinks = footerUrls
    ? getContent(footerUrls.sys.id, entries)?.fields.links.map((link) =>
        getContent(link.sys.id, entries)
      )
    : [];

  const footerMenu =
    footerLinks.length > 0
      ? footerLinks.map((link) => {
          const menuContent = getContent(link.sys.id, entries)?.fields;

          // get page preview & url per link
          const page =
            menuContent?.pages && menuContent?.pages.length
              ? getContent(menuContent?.pages[0].sys.id, entries)
              : menuContent?.page
              ? getContent(menuContent?.page?.sys?.id, entries)
              : null;

          return {
            ...menuContent,
            pageUrl: page?.fields?.slug,
            pageContent: {
              cloudinaryPreviewAssets: page?.fields?.cloudinaryPreviewAssets,
              previewAsset: page?.fields?.previewAsset,
              previewText: getContent(
                page?.fields?.previewText?.sys?.id,
                entries
              ),
            },
          };
        })
      : [];

  return (
    <div className="flex flex-col">
      <div className="block bg-quality-gold h-[5px] w-full" />
      <div
        className="aaco-footer hidden-print mt-0 p-[30px] bg-progressive-blue"
        aria-label={name}
      >
        <div className="flex flex-col w-full max-w-full mx-auto px-0">
          {navigationMenu.length && (
            <div className="aaco-footer__navigation mb-12 order-3 lg:order-1 mb-[72px]">
              <ul className="flex flex-col justify-center items-center lg:flex-row lg:gap-[36px]">
                {navigationMenu.map((link, index) => {
                  return (
                    <li key={index} className="flex items-center h-[55px] lg:h-auto">
                      <CtaWrapper
                        url={link?.url}
                        page={link?.pageUrl}
                        onClick={(e) => {
                          analytics({
                            window,
                            document,
                            detail: getClickFooterNavigation(e, {
                              url: link?.pageUrl,
                            }),
                          })
                          if (locale == defaultLocale) {
                            window.location.href = `/${link?.pageUrl}`;
                          } else {
                            window.location.href = `/${locale}/${link?.pageUrl}`
                          }
                        }}
                        classNames={"aaco-underline aaco-underline--natural-cream"}
                      >
                        <Text tag="body" style={`${`/${link?.pageUrl}` === asPath ? "font-bold" : ""}`}>
                          {link?.name}
                        </Text>
                      </CtaWrapper>
                    </li>
                  );
                })}
                <a href={process.env.NEXT_PUBLIC_PORTAL_LINK} target="_blank" rel="noopener noreferrer" className="flex flex-row h-[55px] lg:h-auto items-center text-natural-cream gap-[6px]">
                  <Text style='aaco-underline aaco-underline--natural-cream'>
                    Distributor&apos;s Portal
                  </Text>
                  <Icon name="external-link" height={18} width={18} className={"text-natural-cream translate-y-[1.5px]"} />
                </a>
                <LangSwitcher
                  domains={domainLocales}
                  locales={locales}
                  locale={locale}
                  path={asPath}
                />
              </ul>
            </div>
          )}
          {!!showLogo && (
            <div className="aaco-footer__logo flex justify-center mb-12 lg:mb-6 lg:mb-12 order-1 lg:order-2">
              <DDLogo width={100} height={116} />
            </div>
          )}
          <div className="aaco-footer__social-mobile flex justify-center lg:hidden order-2 w-full mb-12">
            {socialLinks.length && (
              <ul className="mb-0 p-0 flex justify-center items-center">
                {socialLinks.map((link, index) => (
                  <li key={index} className="block mx-2">
                    <a
                      href={link.fields?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.fields?.name}
                      onClick={(e) => {
                        analytics({
                          window,
                          document,
                          detail: getClickSocialDetail(e, {
                            url: link.fields?.link,
                            platform: link.fields?.name,
                          }),
                        });
                      }}
                      className="flex flex-col w-8 h-8 bg-transparent hover:bg-natural-cream border-1 border-natural-cream text-natural-cream hover:text-progressive-blue justify-center items-center rounded-full p-2"
                    >
                      <Icon
                        name={link.fields?.icon}
                        className="block"
                        width={15}
                        height={15}
                      />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="aaco-footer__bottom order-4 lg:order-3 pt-[30px] lg:pt-0">
            <div className="flex flex-col lg:grid lg:grid-cols-12 gap-x-2 lg:gap-x-10 flex-wrap">
              <div className="aaco-footer__copyright flex flex-col lg:flex-row items-center lg:col-span-5 dd-en:order-2 dd-kr:order-2 order-1 lg:order-1 lg:dd-kr:order-1 lg:dd-en:order-1 w-full flex-1 justify-center lg:justify-start">
                <Text tag="small" style="!text-40-white-on-dark-blue">
                  {copyrightText}
                  {showVersion && (
                    <small>&nbsp;&nbsp;v{publicRuntimeConfig?.version}</small>
                  )}
                </Text>
              </div>
              <div className="aaco-footer__social hidden lg:block order-1 lg:order-2 lg:col-span-2 w-full mb-[60px] lg:my-0 lg:mb-0 lg:mb-0">
                {socialLinks.length && (
                  <ul className="mb-0 p-0 flex justify-center items-center">
                    {socialLinks.map((link, index) => (
                      <li key={index} className="block mx-2">
                        <a
                          href={link.fields?.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={link.fields?.name}
                          onClick={(e) => {
                            analytics({
                              window,
                              document,
                              detail: getClickSocialDetail(e, {
                                url: link.fields?.link,
                                platform: link.fields?.name,
                              }),
                            });
                          }}
                          className="flex flex-col w-8 h-8 bg-transparent hover:bg-natural-cream border-1 border-natural-cream text-natural-cream hover:text-progressive-blue justify-center items-center rounded-full p-2"
                        >
                          <Icon
                            name={link.fields?.icon}
                            className="block"
                            width={15}
                            height={15}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="aaco-footer__links lg:col-span-5 justify-end order-3">
                {footerMenu.length && (
                  <ul className="flex flex-col justify-center items-center lg:justify-end lg:flex-row gap-[36px]">
                    {footerMenu.map((link, index) => {
                      return (
                        <li key={index} className="block">
                          <CtaWrapper
                            url={link?.url}
                            page={link?.pageUrl}
                            onClick={(e) =>
                              analytics({
                                window,
                                document,
                                detail: getClickFooterNavigation(e, {
                                  url: link?.pageUrl,
                                }),
                              })
                            }
                            classNames={"aaco-underline aaco-underline--40-white-on-dark-blue"}
                          >
                            <Text tag="small" style={`${`/${link?.pageUrl}` === asPath ? "font-bold" : ""} !text-40-white-on-dark-blue`}>
                              {link?.name}
                            </Text>
                          </CtaWrapper>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// @TODO: define real props for the Footer based on it requirements
Footer.propTypes = {
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default Footer;
